import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pentestApi } from '../services/api';
import {
  Box, Button, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Select,
  MenuItem, FormControl, InputLabel, Alert, CircularProgress, Grid
} from '@mui/material';
import {
  ArrowBack, Download as DownloadIcon, TrendingUp, TrendingDown
} from '@mui/icons-material';

const PenetrationTestComparison = () => {
  const { pentestId } = useParams();
  const navigate = useNavigate();
  const [pentests, setPentests] = useState([]);
  const [selectedPentest, setSelectedPentest] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPentests();
  }, [pentestId]);

  const fetchPentests = async () => {
    try {
      setLoading(true);
      const currentPentest = await pentestApi.get(pentestId);
      const allPentests = await pentestApi.list({ test_type: currentPentest.test_type });
      
      // Filter out current test and only show tests with analysis
      const comparablePentests = allPentests.filter(p => 
        p.id !== parseInt(pentestId) && p.has_analysis
      );
      
      setPentests(comparablePentests);
    } catch (err) {
      console.error('Error fetching pentests:', err);
      setError('Failed to load penetration tests');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!selectedPentest) {
      setError('Please select a penetration test to compare with');
      return;
    }

    try {
      setComparing(true);
      setError(null);
      const result = await pentestApi.compare(selectedPentest, pentestId);
      setComparison(result);
    } catch (err) {
      setError('Failed to generate comparison: ' + (err.message || 'Unknown error'));
    } finally {
      setComparing(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedPentest) {
      setError('Please select a penetration test to compare with');
      return;
    }

    try {
      setDownloading(true);
      setError(null);
      await pentestApi.downloadComparisonReport(selectedPentest, pentestId);
    } catch (err) {
      setError('Failed to download trending report: ' + (err.message || 'Unknown error'));
    } finally {
      setDownloading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = { 'Critical': 'error', 'High': 'warning', 'Medium': 'info', 'Low': 'success', 'Info': 'default' };
    return colors[severity] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/penetration-tests/${pentestId}`)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">Penetration Test Trending</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Select Test to Compare</Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl fullWidth>
              <InputLabel>Previous Penetration Test</InputLabel>
              <Select
                value={selectedPentest}
                label="Previous Penetration Test"
                onChange={(e) => setSelectedPentest(e.target.value)}
              >
                {pentests.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.test_name} ({new Date(p.test_date).toLocaleDateString()})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleCompare}
              disabled={comparing || !selectedPentest}
            >
              {comparing ? <CircularProgress size={24} /> : 'Compare'}
            </Button>
            <Button
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={downloading || !selectedPentest}
            >
              Download Trending Report
            </Button>
          </Box>
        </CardContent>
      </Card>

      {comparison && (
        <>
          {/* Risk Score Trend */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Risk Score Trend</Typography>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="body2" color="textSecondary">Previous Score</Typography>
                    <Typography variant="h4">{comparison.pentest1.overall_risk_score.toFixed(1)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    bgcolor: comparison.risk_score_change < 0 ? 'success.light' : 
                             comparison.risk_score_change > 0 ? 'error.light' : 'grey.300'
                  }}>
                    <Typography variant="body2" color="textSecondary">Change</Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                      {comparison.risk_score_change < 0 ? <TrendingDown /> : <TrendingUp />}
                      <Typography variant="h4">
                        {comparison.risk_score_change > 0 ? '+' : ''}{comparison.risk_score_change.toFixed(1)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="body2" color="textSecondary">Current Score</Typography>
                    <Typography variant="h4">{comparison.pentest2.overall_risk_score.toFixed(1)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Severity Trend */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Findings by Severity</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell align="center"><strong>Previous</strong></TableCell>
                      <TableCell align="center"><strong>Change</strong></TableCell>
                      <TableCell align="center"><strong>Current</strong></TableCell>
                      <TableCell align="center"><strong>Trend</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparison.severity_trend && Object.entries(comparison.severity_trend).map(([severity, data]) => (
                      <TableRow key={severity}>
                        <TableCell>
                          <Chip 
                            label={severity.charAt(0).toUpperCase() + severity.slice(1)} 
                            color={getSeverityColor(severity.charAt(0).toUpperCase() + severity.slice(1))} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell align="center">{data.previous}</TableCell>
                        <TableCell align="center">
                          <Typography color={data.change < 0 ? 'success.main' : data.change > 0 ? 'error.main' : 'text.primary'}>
                            {data.change > 0 ? '+' : ''}{data.change}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{data.current}</TableCell>
                        <TableCell align="center">
                          {data.change < 0 ? (
                            <TrendingDown color="success" />
                          ) : data.change > 0 ? (
                            <TrendingUp color="error" />
                          ) : (
                            <Typography>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* New Findings */}
          {comparison.new_findings && comparison.new_findings.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error">
                  New Findings ({comparison.new_findings.length})
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Finding</strong></TableCell>
                        <TableCell><strong>Severity</strong></TableCell>
                        <TableCell><strong>Affected Hosts</strong></TableCell>
                        <TableCell><strong>Risk</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.new_findings.map((finding) => (
                        <TableRow key={finding.id}>
                          <TableCell>{finding.finding_name}</TableCell>
                          <TableCell>
                            <Chip label={finding.severity} color={getSeverityColor(finding.severity)} size="small" />
                          </TableCell>
                          <TableCell>{finding.affected_hosts || 'N/A'}</TableCell>
                          <TableCell>{finding.risk_explanation || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Remediated Findings */}
          {comparison.remediated_findings && comparison.remediated_findings.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="success.main">
                  Remediated Findings ({comparison.remediated_findings.length})
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Finding</strong></TableCell>
                        <TableCell><strong>Severity</strong></TableCell>
                        <TableCell><strong>Affected Hosts</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.remediated_findings.map((finding) => (
                        <TableRow key={finding.id}>
                          <TableCell>{finding.finding_name}</TableCell>
                          <TableCell>
                            <Chip label={finding.severity} color={getSeverityColor(finding.severity)} size="small" />
                          </TableCell>
                          <TableCell>{finding.affected_hosts || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                    <Typography variant="h4">{comparison.summary.new_count}</Typography>
                    <Typography variant="body2">New Findings</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4">{comparison.summary.remediated_count}</Typography>
                    <Typography variant="body2">Remediated</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h4">{comparison.summary.persistent_count}</Typography>
                    <Typography variant="body2">Persistent</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default PenetrationTestComparison;
